import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Typography,
  Grid,
  Button,
  Box,
  InputLabel,
  Skeleton,
  TextField,
  Autocomplete,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import DatePickerMonthEnds from "../../common/DatePicker/DatePickerMonthEnds";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import { useUserContext } from "../../../context/UserContext";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import CashflowTable from "./tables/CashflowTable.js";
import InvestmentsTable from "./tables/InvestmentsTable.js";
import CommercialPaperTable from "./tables/CommercialPaperTable";
import CreditFacilityTable from "./tables/CreditFacilityTable.js";
import STReceivablesOnBsTable from "./tables/STReceivablesOnBsTable.js";
import STLiabilitiesOnBsTable from "./tables/STLiabilitiesOnBsTable.js";
import TimelineTable from "./tables/TimelineTable.js";
import { creditFacilityResponse } from "./responseApi";
import { mainResponse } from "./responseApi.js";
import { downloadElementsAsPdf } from "../../common/Pdf/pdfDownloader.js";
import {
  getSimpleFormattedDate,
  minDate,
  maxDate,
  getMappedBusinessEntityIDs,
} from "../../../utils/utils.js";

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const DAF2TreasuryFiltersAndReports = () => {
  const [creditFacilityTableDatas, setCreditFacilityTableDatas] = useState([]);
  const [liquidityModelTableColumns, setLiquidityModelTableColumns] = useState(
    [],
  );
  const [liquidityModelTableDatas, setLiquidityModelTableDatas] = useState([]);
  const [investmentsTableDatas, setInvestmentsTableDatas] = useState([]);
  const [sTReceivablesOnBsTableDatas, setSTReceivablesOnBsTableDatas] =
    useState([]);
  const [sTLiabilitiesOnBsTableDatas, setSTLiabilitiesOnBsTableDatas] =
    useState([]);

  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();
  const [isTouched, setIsTouched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedMonthEnd,
    setSelectedMonthEnd,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    allowedMonthEnds,
    localCurrency,
    setLocalCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    setSelectedScenarioMnemonics,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
  } = useHedgeRatioContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  useEffect(() => {
    // For HR KRI reporting analytics, default scenario is always 'BaseCurrentPosition'
    setSelectedScenarioMnemonics("BaseCurrentPosition");
  }, [setSelectedScenarioMnemonics]);

  useEffect(() => {
    if (selectedBusinessEntityID) {
      const entityObject = hrFiltersData?.find(
        (item) => item.entity === selectedBusinessEntityID,
      );
      const filteredModelDatabases = (entityObject?.dateModels || []).filter(
        (model) => model?.treasuryKRIGlobalView === true,
      );
      setModelDatabases((prev) => {
        if (
          prev.length === filteredModelDatabases?.length &&
          prev.every(
            (item, index) =>
              item.modelName === filteredModelDatabases[index]?.modelName,
          )
        ) {
          return prev;
        }
        return filteredModelDatabases;
      });
      const { localCurrency: currency } =
        allsfcs?.find(
          (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
        ) || {};
      setLocalCurrency(currency);
    }
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  useEffect(() => {
    const modelObject = modelDatabases?.find((model) => {
      if (model?.monthEnd) {
        const datePart = model.monthEnd?.slice(0, 10);
        return datePart === selectedMonthEnd?.format("YYYY-MM-DD");
      }
      return false;
    });
    setSelectedModelDatabase(modelObject?.modelName || null);
  }, [modelDatabases, selectedMonthEnd, setSelectedModelDatabase]);

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  const handleSubmitBtn = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      alert("Mocked!");
    } else {
      // setData([]);
      alert(
        "The selected Entity / Month End does not have any Model Database associated (or) it does not have Treasury KRI Global View.",
      );
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setIsTouched(true);
    }
    setIsOpen(open);
  };

  const reportRef = useRef(null);

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    const elementsToPrint = [reportRef.current];
    await downloadElementsAsPdf(
      elementsToPrint,
      `DAF2TreasuryKri_${getSimpleFormattedDate()}`,
    );
    setIsExporting(false);
  };

  const [isExporting, setIsExporting] = useState(false);

  return (
    <>
      <Card
        style={{
          padding: "6px",
          marginBottom: "20px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "#6a6a6a",
            marginBottom: 2,
            fontWeight: "bold",
          }}
        >
          The below data shows DAF2 Template - Liquidity Risk (Global View) for
          the selected model database.
        </Typography>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id={"business-entity"}>Business Entity</InputLabel>
            <Autocomplete
              options={mappedBusinessEntityIDs}
              getOptionLabel={(option) => option?.name || ""}
              value={
                mappedBusinessEntityIDs.find(
                  (item) => item.id === selectedBusinessEntityID,
                ) || null
              }
              onChange={(_, newValue) => {
                setSelectedBusinessEntityID(newValue ? newValue.id : null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={selectedUserDetailLoading || hrFiltersDataLoading}
              ListboxProps={{
                style: {
                  width: "max-content",
                  minWidth: "100%",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder="Select Business Entity"
                  size="small"
                />
              )}
              sx={{ width: 180 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id={"report-date"}>Month End & Year</InputLabel>
            {selectedUserDetailLoading || hrFiltersDataLoading ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Skeleton variant="rectangular" width={185} height={40} />
              </Box>
            ) : (
              <>
                <Box display="flex" alignItems="center" gap={2} mb={0}>
                  <DatePickerMonthEnds
                    minDate={minDate}
                    maxDate={maxDate}
                    allowedMonthEnds={allowedMonthEnds}
                    value={selectedMonthEnd}
                    onChange={(newValue) => {
                      setSelectedMonthEnd(newValue?.endOf("month"));
                    }}
                    onOpenChange={handleOpenChange}
                    isOpen={isOpen}
                  />
                </Box>
                <Box>
                  {selectedMonthEnd === null && isTouched && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        fontStyle: "italic",
                      }}
                    >
                      Month End & Year is required
                    </span>
                  )}
                </Box>
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmitBtn}
              sx={{
                fontSize: "0.8rem",
                boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                height: "38px",
                mt: 3,
              }}
              disabled={
                selectedUserDetailLoading ||
                hrFiltersDataLoading ||
                !selectedBusinessEntityID ||
                !selectedMonthEnd
              }
            >
              Submit
            </Button>
          </Grid>
          {/* <Grid
            item
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <div className={classes.DAF2ExportBtn}>
              <DAF2TreasuryExport columns={[]} datas={[]} disabled={true} />
            </div>
          </Grid> */}
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<PictureAsPdf />}
              onClick={handleDownloadPDF}
              sx={{
                fontSize: "0.8rem",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                height: "40px",
              }}
              disabled={
                selectedUserDetailLoading ||
                !selectedBusinessEntityID ||
                !selectedMonthEnd ||
                isExporting
              }
            >
              Export to PDF
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InlineDropdown
              label="Show Figures In"
              labelId="show-figures-in"
              options={mappedFigureDivisors}
              selectedValue={selectedFigureDivisor}
              handleChange={setSelectedFigureDivisor}
              placeholder={"Show Figures In"}
              dropdownWidth={150}
              isLoading={selectedUserDetailLoading || hrFiltersDataLoading}
            />
          </Grid>
          {localCurrency && (
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id="currency-type">Currency</InputLabel>
              <Typography
                sx={{
                  color: "#6a6a6a",
                  fontWeight: "bold",
                  marginTop: 1,
                }}
              >
                {localCurrency}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <InlineDropdown
              label="Show Decimal In"
              labelId="show-decimal-in"
              options={mappedDecimalsIn}
              selectedValue={selectedDecimalsIn}
              handleChange={setSelectedDecimalsIn}
              placeholder={"Show Decimals In"}
              dropdownWidth={150}
              isLoading={selectedUserDetailLoading || hrFiltersDataLoading}
            />
          </Grid>
        </Grid>
      </Card>

      <>
        {/* Check for data loading condition */}
        {false ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              There was an error in fetching the data. Please try again later.
            </Typography>
          </Item>
        ) : false ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              No data available for the selected filters.
            </Typography>
          </Item>
        ) : (
          <div ref={reportRef}>
            <Card
              style={{
                padding: "6px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <CashflowTable
                mainResponse={mainResponse}
                creditFacilityTableDatas={creditFacilityTableDatas}
                setLiquidityModelTableColumns={setLiquidityModelTableColumns}
                setLiquidityModelTableDatas={setLiquidityModelTableDatas}
              />
            </Card>
            <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <InvestmentsTable
                    setInvestmentsTableDatas={setInvestmentsTableDatas}
                  />
                  <div style={{ marginTop: "2px" }}>
                    <CommercialPaperTable />               
                  </div>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <CreditFacilityTable
                    setCreditFacilityTableDatas={setCreditFacilityTableDatas}
                    creditFacilityResponse={creditFacilityResponse}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <STReceivablesOnBsTable
                    setSTReceivablesOnBsTableDatas={
                      setSTReceivablesOnBsTableDatas
                    }
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <STLiabilitiesOnBsTable
                    setSTLiabilitiesOnBsTableDatas={
                      setSTLiabilitiesOnBsTableDatas
                    }
                  />
                </Card>
              </Grid>
            </Grid>
            <Card
              style={{
                padding: "6px",
                marginBottom: "20px",
              }}
            >
              <TimelineTable
                liquidityModelTableColumns={liquidityModelTableColumns}
                liquidityModelTableDatas={liquidityModelTableDatas}
                investmentsTableDatas={investmentsTableDatas}
                sTReceivablesOnBsTableDatas={sTReceivablesOnBsTableDatas}
                sTLiabilitiesOnBsTableDatas={sTLiabilitiesOnBsTableDatas}
              />
            </Card>
          </div>
        )}
      </>
    </>
  );
};

export default DAF2TreasuryFiltersAndReports;
